import { useEffect, useState } from 'react';
import { spellApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { SpellData } from '../../../types/adminTypes';

export function SpellsPage() {
  const [spells,   setSpells]   = useState<SpellData[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [selected, setSelected] = useState<SpellData | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    spellApi.list()
      .then(setSpells)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="adminHeader" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1>{t('spells.title')}</h1>
        <button 
          title="Ver Guia de Gestão e Distribuição dos Poderes"
          onClick={() => setShowGuide(true)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, padding: 0,
            transition: 'transform 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          🍃
        </button>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

          {/* ── Table ── */}
          <div className="adminCard" style={{ flex: 1 }}>
            <div className="adminCardHeader">
              <span>{t('spells.listHeader')} ({spells.length})</span>
              <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 400 }}>
                {t('spells.readOnly')}
              </span>
            </div>

            {loading ? (
              <div className="spinner" />
            ) : spells.length === 0 ? (
              <div className="emptyState">{t('spells.empty')}</div>
            ) : (
              <table className="adminTable">
                <thead>
                  <tr>
                    <th>{t('spells.col.id')}</th>
                    <th>{t('spells.col.name')}</th>
                    <th>{t('spells.col.apCost')}</th>
                    <th>{t('spells.col.range')}</th>
                    <th>{t('spells.col.area')}</th>
                    <th>{t('spells.col.castsPerTurn')}</th>
                    <th>{t('spells.col.cooldown')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {spells.map((s) => (
                    <tr
                      key={s.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelected(selected?.id === s.id ? null : s)}
                    >
                      <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-dim)' }}>{s.id}</td>
                      <td style={{ fontWeight: 600 }}>{t(`spell.${s.id}.name` as any) || s.name}</td>
                      <td><span className="badge">{s.ap_cost} AP</span></td>
                      <td style={{ color: 'var(--text-dim)' }}>
                        {s.range_min === s.range_max
                          ? s.range_min
                          : `${s.range_min}–${s.range_max}`}
                      </td>
                      <td style={{ color: 'var(--text-dim)', textTransform: 'capitalize' }}>
                        {s.effect_area.type}
                        {s.effect_area.size > 0 && ` ${s.effect_area.size}`}
                      </td>
                      <td style={{ color: 'var(--text-dim)' }}>{s.casts_per_turn}</td>
                      <td style={{ color: 'var(--text-dim)' }}>{s.cooldown}</td>
                      <td>
                        <button
                          className="btn btnGhost"
                          style={{ padding: '3px 8px', fontSize: 11 }}
                          onClick={(e) => { e.stopPropagation(); setSelected(selected?.id === s.id ? null : s); }}
                        >
                          {selected?.id === s.id ? t('spells.close') : t('spells.details')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Detail panel ── */}
          {selected && (
            <div className="adminCard" style={{ width: 280, flexShrink: 0 }}>
              <div className="adminCardHeader">
                <span>{t(`spell.${selected.id}.name` as any) || selected.name}</span>
                <button
                  style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                  onClick={() => setSelected(null)}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {selected.icon_url && (
                  <div style={{ textAlign: 'center', marginBottom: 10 }}>
                     <img src={selected.icon_url} alt={selected.name} style={{ width: 64, height: 64, borderRadius: 8, background: '#111' }} />
                  </div>
                )}
                {(t(`spell.${selected.id}.desc` as any) || selected.description) && (
                  <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: 0, lineHeight: 1.5 }}>
                    {t(`spell.${selected.id}.desc` as any) || selected.description}
                  </p>
                )}

                <DetailRow label="ID"                          value={selected.id} mono />
                {selected.shortcut && <DetailRow label="Shortcut" value={selected.shortcut} mono />}
                <DetailRow label={t('spells.detail.cost')}     value={`${selected.ap_cost} ${t('spells.detail.ap')}`} />
                <DetailRow
                  label={t('spells.detail.range')}
                  value={selected.range_min === selected.range_max
                    ? String(selected.range_min)
                    : `${selected.range_min} – ${selected.range_max}`}
                />
                <DetailRow
                  label={t('spells.detail.lineOfSight')}
                  value={selected.line_of_sight ? t('spells.detail.yes') : t('spells.detail.no')}
                />
                <DetailRow
                  label={t('spells.detail.area')}
                  value={`${selected.effect_area.type} (${selected.effect_area.size})`}
                />
                <DetailRow label={t('spells.detail.castsPerTurn')} value={String(selected.casts_per_turn)} />
                <DetailRow
                  label={t('spells.detail.cooldown')}
                  value={selected.cooldown === 0
                    ? t('spells.detail.none')
                    : `${selected.cooldown} ${t('spells.detail.turns')}`}
                />

                {selected.effects.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-dim)', marginBottom: 6 }}>
                      {t('spells.detail.effects')}
                    </div>
                    {selected.effects.map((ef, i) => (
                      <div key={i} style={{ fontSize: 12, padding: '5px 8px', background: 'rgba(68,204,136,0.07)', borderRadius: 4, marginBottom: 4 }}>
                        <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{ef.type}</span>
                        {ef.element && <span style={{ color: 'var(--text-dim)' }}> ({ef.element})</span>}
                        <span style={{ float: 'right', color: 'var(--accent2)' }}>
                          {ef.min_value}–{ef.max_value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Guia de Magias */}
      {showGuide && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }} onClick={() => setShowGuide(false)}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '24px 32px',
            maxWidth: 800, width: '90%', maxHeight: '85vh', overflowY: 'auto', position: 'relative'
          }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowGuide(false)} style={{
              position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 20
            }}>✕</button>
            <h2 style={{ color: 'var(--accent)', marginTop: 0, marginBottom: 16 }}>Gestão e Distribuição dos Poderes (Spells)</h2>
            <p style={{ fontSize: 14, color: 'var(--text)' }}>Este guia define a estrutura ideal para a distribuição de poderes entre todas as classes do jogo, mantendo o equilíbrio e garantindo uma identidade única (roleplay) para cada personagem.</p>

            <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8, marginTop: 24, color: '#ffb84d' }}>1. Poderes Comuns (Globais)</h3>
            <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>Todo e qualquer personagem recém-criado deverá possuir essas habilidades fundamentais de sobrevivência.</p>
            <ul style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 20 }}>
              <li style={{ marginBottom: 6 }}><strong>Golpe Básico (3 AP | 1 Alcance | Neutro):</strong> Um ataque físico mundano com arma branca.</li>
              <li><strong>Esquiva Tática (2 AP | Si Mesmo | Buff):</strong> Aumenta o movimento (+1 MP) temporariamente para escapar.</li>
            </ul>

            <h3 style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8, marginTop: 24, color: '#ffb84d' }}>2. Poderes Específicos por Classe</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <h4 style={{ color: '#c87c30', margin: '0 0 8px 0' }}>🛡️ Guerreiro (Tank & Melee Bruiser)</h4>
                <ul style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 20, margin: 0 }}>
                  <li><strong>Golpe Sísmico:</strong> Bate no chão causando terremoto ao redor. Atordoa inimigos.</li>
                  <li><strong>Grito de Batalha:</strong> Aumenta a Força de aliados próximos e intimida inimigos.</li>
                  <li><strong>Investida Colossal:</strong> Avança rapidamente em linha reta contra um inimigo.</li>
                </ul>
              </div>
              <div>
                <h4 style={{ color: '#8060d0', margin: '0 0 8px 0' }}>🔮 Mago (Ranged DPS & AoE)</h4>
                <ul style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 20, margin: 0 }}>
                  <li><strong>Explosão Arcana:</strong> Alto dano em área, requer linha de visão.</li>
                  <li><strong>Barreira Arcana:</strong> Cria um escudo mágico temporário para mitigar danos.</li>
                  <li><strong>Raio Congelante:</strong> Reduz os PM do alvo, prendendo-o.</li>
                </ul>
              </div>
              <div>
                <h4 style={{ color: '#40a060', margin: '0 0 8px 0' }}>🗡️ Ladino (Assassin & Flank)</h4>
                <ul style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 20, margin: 0 }}>
                  <li><strong>Lâmina Sombria:</strong> Golpes penetrantes corpo a corpo que ignoram parcialmente a armadura.</li>
                  <li><strong>Passo Sombrio:</strong> Fica invisível e ganha Esquiva para reposicionamento seguro.</li>
                  <li><strong>Armadilha Venenosa:</strong> Posiciona armadilha invisível no chão que causa dano ao ser pisada.</li>
                </ul>
              </div>
              <div>
                <h4 style={{ color: '#30a0c0', margin: '0 0 8px 0' }}>🏹 Arqueiro (Ranged Sniper)</h4>
                <ul style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 20, margin: 0 }}>
                  <li><strong>Flecha do Destino:</strong> Disparo com alcance enorme (2 a 8 blocos), escalando com Sorte.</li>
                  <li><strong>Vento Favorável:</strong> Aumenta Iniciativa e Esquiva de todo o grupo em volta.</li>
                  <li><strong>Tiro Repelente:</strong> Causa dano e empurra o inimigo atacado 2 blocos para trás.</li>
                </ul>
              </div>
              <div>
                <h4 style={{ color: '#e0c040', margin: '0 0 8px 0' }}>✨ Clérigo (Support & Healer)</h4>
                <ul style={{ fontSize: 13, color: 'var(--text)', paddingLeft: 20, margin: 0 }}>
                  <li><strong>Luz Reveladora:</strong> Cura aliados na área da magia e retira Invisibilidade de inimigos.</li>
                  <li><strong>Julgamento Sagrado:</strong> Um martelo divino acerta uma área em cruz, focado em queimar inimigos.</li>
                  <li><strong>Vínculo Vital:</strong> Conecta seu HP ao de um aliado, dividindo o dano recebido.</li>
                </ul>
              </div>
            </div>
            
          </div>
        </div>
      )}
    </>
  );
}

// ── Detail row helper ─────────────────────────────────────────────────────────
function DetailRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
      <span style={{ color: 'var(--text-dim)' }}>{label}</span>
      <span style={{ fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 11 : undefined }}>{value}</span>
    </div>
  );
}
